/**
 * PermissionRoute Component
 *
 * SECURITY: VULN-018 FIX - Client-Side Permission Checks Documentation
 *
 * ⚠️ CRITICAL SECURITY NOTICE ⚠️
 *
 * This component provides UI-level permission checks ONLY.
 * These checks are for USER EXPERIENCE purposes and DO NOT provide security enforcement.
 *
 * Purpose:
 * - Hide UI elements that users shouldn't see (better UX)
 * - Provide early feedback about permission requirements
 * - Reduce unnecessary API calls that would fail anyway
 *
 * Security Limitations:
 * - Client-side checks can be bypassed by modifying browser code
 * - JavaScript can be disabled or manipulated
 * - Permission data in localStorage/memory can be altered
 * - DevTools can modify React component behavior
 *
 * ⚠️ BACKEND MUST ALWAYS VALIDATE PERMISSIONS ⚠️
 *
 * The backend API MUST implement its own permission validation:
 * 1. Validate user authentication on EVERY request
 * 2. Check user permissions against required permission
 * 3. Return 403 Forbidden if user lacks required permissions
 * 4. Log permission denials for security monitoring
 * 5. NEVER trust client-side permission checks
 *
 * Defense in Depth:
 * - Client-side: Improves UX by hiding unauthorized UI
 * - Backend: Enforces security by rejecting unauthorized requests
 * - Both layers work together but backend is the security boundary
 *
 * Example Attack Scenario:
 * 1. Attacker opens DevTools
 * 2. Modifies hasPermission() to always return true
 * 3. UI now shows admin features
 * 4. Attacker clicks "Delete All Users"
 * 5. Backend receives request, validates permissions, returns 403 Forbidden
 * 6. Attack is blocked by backend validation
 *
 * See Documentation:
 * - docs/SECURITY_ARCHITECTURE.md for detailed permission architecture
 * - docs/SECURITY.md for general security guidelines
 *
 * Testing:
 * - Manually test bypassing client checks (should fail at backend)
 * - Verify backend returns 403 for unauthorized requests
 * - Check security logs for permission denial attempts
 */

import { LockOutlined } from '@mui/icons-material';
import { Box, Alert, Typography, Button, Skeleton, Stack } from '@mui/material';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/permissions/usePermissions';

interface PermissionRouteProps {
  children: React.ReactNode;
  permission?: string;
  fallbackPath?: string;
}

/**
 * Check if user has a specific permission based on backend permissions
 *
 * ⚠️ SECURITY: This is a UI-level check only. Backend must also validate.
 *
 * @param permissions - User permissions object from backend
 * @param permission - Required permission key
 * @returns true if user has permission (UI check only)
 */
const hasPermission = (permissions: any, permission: string): boolean => {
  if (!permissions) {return false;}
  return permissions[permission]?.is_visible ?? false;
};

/**
 * Access denied component
 */
const AccessDenied: React.FC<{ permission: string; fallbackPath?: string }> = ({
  permission,
  fallbackPath = "/dashboard"
}) => {
  const { user } = usePermissions();

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      p={4}
    >
      <LockOutlined
        sx={{
          fontSize: 64,
          color: 'text.secondary',
          mb: 2
        }}
      />
      <Typography variant="h5" gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" mb={3}>
        You need <strong>{getPermissionDisplayName(permission)}</strong> permissions to access this page.
      </Typography>
      <Alert severity="info" sx={{ mb: 2, maxWidth: 500 }}>
        Contact your administrator to upgrade your permissions if you need access to this feature.
      </Alert>
      <Button
        variant="contained"
        onClick={() => window.location.href = fallbackPath}
      >
        Go to Dashboard
      </Button>
    </Box>
  );
};

/**
 * Route component that checks both authentication and specific permissions.
 * Shows permission denied page instead of redirect for better UX.
 *
 * ⚠️ SECURITY NOTE: This is a UI-level permission gate only.
 * All API endpoints accessed by children components MUST validate
 * permissions on the backend. This component only prevents rendering
 * unauthorized UI elements - it does NOT prevent API access.
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  fallbackPath = "/dashboard"
}) => {
  const { permissions, isLoading, user } = usePermissions();

  // Check if user is authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // No permission required - render immediately
  if (!permission) {
    return <>{children}</>;
  }

  // Show loading skeleton while permissions are loading
  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} />
        <Skeleton variant="rectangular" height={400} />
        <Skeleton variant="rectangular" height={200} />
      </Stack>
    );
  }

  // ⚠️ SECURITY: UI-level permission check (UX only, not security enforcement)
  // Backend must also validate permissions on all API endpoints
  if (!hasPermission(permissions, permission)) {
    return <AccessDenied permission={permission} fallbackPath={fallbackPath} />;
  }

  // User has permission, render children
  // IMPORTANT: Child components must still validate on backend API calls
  return <>{children}</>;
};

/**
 * Get user-friendly name for permission
 */
function getPermissionDisplayName(permission: string): string {
  const permissionNames = {
    'my_groups': 'My Groups',
    'users': 'User Management',
    'groups': 'Group Management',
    'faq_moderation': 'FAQ Moderation', 
    'chat_config': 'Chat Configuration',
    'embedding_config': 'Embedding Configuration',
    'text_extraction_config': 'Text Extraction Configuration',
    'document_sources': 'Document Sources',
    'system_management': 'System Management',
    'organization': 'Organization Management',
    'all_organizations': 'All Organizations Management',
    'data_management': 'Data Management'
  };
  
  return permissionNames[permission as keyof typeof permissionNames] || permission;
}

